/**
 * UTM attribution — Clerk user + database persistence.
 *
 * After a visitor signs in or creates an account, any UTM parameters that were
 * captured by `useUtmCapture` (and stored in localStorage) are written to:
 *
 *  1. The Clerk user's `unsafeMetadata` under the key `acquisitionUtm` —
 *     accessible from the Clerk dashboard / user API.
 *  2. `PATCH /users/me` with `acquisitionSource` — durably stored in the
 *     `users.acquisition_source` column so it survives a private/incognito
 *     session (where localStorage is cleared) and is queryable straight from
 *     the database, not just from Clerk.
 *
 * Both writes are fire-and-forget (best-effort) and never block the user
 * experience. Each is skipped independently once already recorded.
 *
 * Retry behaviour: this hook lives in the app shell and is typically mounted
 * exactly once per session, so a failed write cannot rely on "the next
 * mount" to retry it — there usually isn't one until a full page reload.
 * Instead, on failure each write schedules its own retry with backoff
 * (RETRY_DELAYS_MS) so a transient blip (network hiccup, user row not yet
 * provisioned) doesn't silently and permanently lose attribution for the
 * rest of the session.
 */

import { useEffect, useRef } from 'react';
import { useAuth, useUser } from '@clerk/react';
import { getStoredUtmParams } from './use-utm';

// Exported for tests. Capped, short backoff — this is best-effort attribution,
// not a critical user-facing action, so we don't retry forever.
export const RETRY_DELAYS_MS = [2_000, 10_000, 30_000];

export function useUtmAttribution(): void {
  const { isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const clerkDone = useRef(false);
  const clerkRetryCount = useRef(0);
  const apiDone = useRef(false);
  const apiRetryCount = useRef(0);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (!isSignedIn || !user) return;

    const utmParams = getStoredUtmParams();
    if (!utmParams || Object.keys(utmParams).length === 0) return;

    let cancelled = false;

    // 1. Clerk metadata (best-effort, independent of the API write below).
    function attemptClerkWrite() {
      if (clerkDone.current || cancelled || user!.unsafeMetadata?.acquisitionUtm) return;
      user!
        .update({
          unsafeMetadata: {
            ...user!.unsafeMetadata,
            acquisitionUtm: utmParams,
          },
        })
        .then(() => {
          clerkDone.current = true;
        })
        .catch(() => {
          if (cancelled) return;
          const delay = RETRY_DELAYS_MS[clerkRetryCount.current];
          if (delay === undefined) return; // out of retries — give up quietly
          clerkRetryCount.current += 1;
          timers.current.push(setTimeout(attemptClerkWrite, delay));
        });
    }

    // 2. Durable database write, so attribution survives localStorage clears
    // and is queryable straight from the users table.
    async function attemptApiWrite() {
      if (apiDone.current || cancelled) return;
      try {
        const token = await getToken();
        const res = await fetch('/api/users/me', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({ acquisitionSource: utmParams }),
        });
        if (res.ok) {
          apiDone.current = true;
          return;
        }
        throw new Error(`PATCH /api/users/me failed: ${res.status}`);
      } catch {
        if (cancelled) return;
        const delay = RETRY_DELAYS_MS[apiRetryCount.current];
        if (delay === undefined) return; // out of retries — give up quietly
        apiRetryCount.current += 1;
        timers.current.push(setTimeout(attemptApiWrite, delay));
      }
    }

    attemptClerkWrite();
    attemptApiWrite();

    return () => {
      cancelled = true;
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, [isSignedIn, user, getToken]);
}
